/**
 * The group Overview's "Per member" table (§8.8) — a comparison surface, not a listing.
 *
 * Thirteen columns, all of them server-computed `Explained` envelopes rendered verbatim (§12.5):
 * the member's totals, its total engagement closing that run, its per-post avg/median, its follower
 * count, its own engagement rate and its posting cadence. The followers column is the rate's
 * denominator, which is what makes two rates
 * comparable; it sits immediately left of ER for that reason. Headers are abbreviated in the
 * inline card and spelled out in the fullscreen Modal (`fullLabels`), where the width exists.
 * Sorting reads `Explained.value` — ordering is not display arithmetic, and it is the same
 * comparator shape the homescreen tables use (nulls sink in both directions, name then id break
 * ties). Nothing here is persisted; the sort resets with the view.
 *
 * A row click toggles that member in the view's `authorIds` filter — the FilterPanel's author
 * checkboxes by another name, so the header badge, the URL-independent persistence and the
 * multi-select semantics all come for free. While the filter is active the *unselected* rows show
 * zeros and "—": their posts are filtered out of the request, exactly as the header filter does.
 *
 * Two things in the member cell are carved out of that toggle, as on the homescreen: the name is a
 * `<Link>` to the member's own view (so new-tab and middle-click work natively) and the ↗ opens
 * them on LinkedIn. The avatar is what stays the filter's keyboard-reachable button.
 */

import { useMemo } from 'react';
import { Link } from 'react-router';
import type { Explained, OverviewMemberDTO } from '@lw/shared';
import { Avatar, ExternalLinkIcon, Num, cx } from '../../../components';
import { viewPath } from '../../../app/routes';
import { SortableHeader, type SortState } from '../../home/SortableHeader';

export type PerMemberSortKey =
  | 'member'
  | 'posts'
  | 'reactions'
  | 'comments'
  | 'shares'
  | 'saves'
  | 'totalEngagement'
  | 'avg'
  | 'median'
  | 'followers'
  | 'engagementRate'
  | 'postsPerWeek'
  | 'postsPerDay';

export type PerMemberSort = SortState<PerMemberSortKey>;

export const DEFAULT_PER_MEMBER_SORT: PerMemberSort = { key: 'posts', dir: 'desc' };

/** Compact header, its spelled-out fullscreen name, and the description both hover. */
const COLUMNS: readonly {
  key: PerMemberSortKey;
  label: string;
  full: string;
  title: string;
}[] = [
  { key: 'posts', label: 'Posts', full: 'Posts', title: 'Counted posts' },
  { key: 'reactions', label: 'React', full: 'Reactions', title: 'Reactions' },
  { key: 'comments', label: 'Cmts', full: 'Comments', title: 'Comments' },
  { key: 'shares', label: 'Shares', full: 'Shares', title: 'Shares' },
  { key: 'saves', label: 'Saves', full: 'Saves', title: 'Saves' },
  {
    key: 'totalEngagement',
    label: 'Total',
    full: 'Total Engagement',
    title: 'Total engagement (reactions + comments + shares + saves) over counted posts',
  },
  {
    key: 'avg',
    label: 'Avg',
    full: 'Avg. Engagement',
    title: 'Average engagement per counted post',
  },
  {
    key: 'median',
    label: 'Med',
    full: 'Med. Engagement',
    title: 'Median engagement per counted post',
  },
  {
    key: 'followers',
    label: 'Flwrs',
    full: 'Followers',
    title: 'Followers — latest count reported in the selected range',
  },
  {
    key: 'engagementRate',
    label: 'ER',
    full: 'Eng. Rate',
    title: 'Engagement rate (total engagement ÷ followers)',
  },
  { key: 'postsPerWeek', label: '/wk', full: 'Posts per week', title: 'Posts per week' },
  { key: 'postsPerDay', label: '/day', full: 'Posts per day', title: 'Posts per day' },
];

/** The one text column; every other key sorts on a number and starts descending. */
const TEXT_SORT_KEYS: PerMemberSortKey[] = ['member'];

function cellOf(row: OverviewMemberDTO, key: PerMemberSortKey): Explained | null {
  switch (key) {
    case 'posts':
      return row.totals.posts;
    case 'reactions':
      return row.totals.reactions;
    case 'comments':
      return row.totals.comments;
    case 'shares':
      return row.totals.shares;
    case 'saves':
      return row.totals.saves;
    case 'totalEngagement':
      return row.totals.totalEngagement;
    case 'avg':
      return row.perPost.totalEngagement.avg;
    case 'median':
      return row.perPost.totalEngagement.median;
    case 'followers':
      return row.followers;
    case 'engagementRate':
      return row.engagementRate;
    case 'postsPerWeek':
      return row.postsPerWeek;
    case 'postsPerDay':
      return row.postsPerDay;
    case 'member':
      return null;
  }
}

function sortRows(rows: readonly OverviewMemberDTO[], sort: PerMemberSort): OverviewMemberDTO[] {
  const factor = sort.dir === 'asc' ? 1 : -1;
  const textual = TEXT_SORT_KEYS.includes(sort.key);

  return [...rows].sort((a, b) => {
    let result: number;
    if (textual) {
      result = a.memberName.localeCompare(b.memberName);
    } else {
      const left = cellOf(a, sort.key)?.value ?? null;
      const right = cellOf(b, sort.key)?.value ?? null;
      // A "—" cell sinks in both directions: sorting by ER must surface measurable members.
      if (left === null || right === null) {
        if (left !== right) return left === null ? 1 : -1;
        result = 0;
      } else {
        result = left - right;
      }
    }
    if (result !== 0) return result * factor;
    return a.memberName.localeCompare(b.memberName) || a.memberId - b.memberId;
  });
}

/** First click: text ascending, numbers descending (the homescreen convention). */
export function nextPerMemberSort(current: PerMemberSort, key: PerMemberSortKey): PerMemberSort {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: TEXT_SORT_KEYS.includes(key) ? 'asc' : 'desc' };
}

export type PerMemberTableProps = {
  rows: OverviewMemberDTO[];
  sort: PerMemberSort;
  onSort: (key: PerMemberSortKey) => void;
  /** The view's `authorIds` filter — the selected rows. */
  activeAuthorIds: number[];
  onToggleMember: (memberId: number) => void;
  mediaEnabled: boolean;
  /** Fullscreen: spell the column names out instead of abbreviating them. */
  fullLabels?: boolean;
};

export function PerMemberTable({
  rows,
  sort,
  onSort,
  activeAuthorIds,
  onToggleMember,
  mediaEnabled,
  fullLabels = false,
}: PerMemberTableProps) {
  const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);

  return (
    <div className="table-wrap">
      <table className="table pm-table">
        <thead>
          <tr>
            <SortableHeader
              label="Member"
              columnKey="member"
              sort={sort}
              onSort={onSort}
              title="Member — click a row to filter the view by that member"
            />
            {COLUMNS.map((column) => (
              <SortableHeader
                key={column.key}
                label={fullLabels ? column.full : column.label}
                columnKey={column.key}
                sort={sort}
                onSort={onSort}
                title={column.title}
                className="num"
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const selected = activeAuthorIds.includes(row.memberId);
            return (
              <tr
                key={row.memberId}
                className={cx('pm-row', selected && 'is-selected')}
                // Convenience target only — the avatar is the real toggle button. A click that
                // started inside another control (a tooltip's (i), the two links in the member
                // cell) must not also toggle the row.
                onClick={(event) => {
                  if ((event.target as HTMLElement).closest('a, button') !== null) return;
                  onToggleMember(row.memberId);
                }}
              >
                <td>
                  {/* Interleaved, like the homescreen name cell: the avatar toggles the filter,
                      the name links to the member's own view and the ↗ opens them on LinkedIn. */}
                  <div className="pm-member">
                    <button
                      type="button"
                      className="pm-member__toggle"
                      aria-pressed={selected}
                      aria-label={`Filter by ${row.memberName}`}
                      onClick={() => onToggleMember(row.memberId)}
                    >
                      <Avatar
                        media={row.avatar}
                        name={row.memberName}
                        mediaEnabled={mediaEnabled}
                        size="sm"
                        kind={row.kind}
                      />
                    </button>
                    <div className="pm-member__line">
                      <Link
                        className="pm-member__name"
                        to={viewPath('profile', row.memberId)}
                        title={row.memberName}
                      >
                        {row.memberName}
                      </Link>
                      {row.url !== null ? (
                        <a
                          className="pm-member__link"
                          href={row.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label={`Open ${row.memberName} on LinkedIn`}
                        >
                          <ExternalLinkIcon size={13} />
                        </a>
                      ) : null}
                    </div>
                  </div>
                </td>
                <td className="num">
                  <Num value={row.totals.posts} withTooltip={false} />
                </td>
                <td className="num">
                  <Num value={row.totals.reactions} withTooltip={false} />
                </td>
                <td className="num">
                  <Num value={row.totals.comments} withTooltip={false} />
                </td>
                <td className="num">
                  <Num value={row.totals.shares} withTooltip={false} />
                </td>
                <td className="num">
                  <Num value={row.totals.saves} withTooltip={false} />
                </td>
                <td className="num">
                  <Num value={row.totals.totalEngagement} withTooltip={false} />
                </td>
                <td className="num">
                  <Num value={row.perPost.totalEngagement.avg} />
                </td>
                <td className="num">
                  <Num value={row.perPost.totalEngagement.median} />
                </td>
                {/* Both of these carry a "—" whose reason only the tooltip can give: the follower
                    count nothing reported, and the rate that count leaves undividable. */}
                <td className="num">
                  <Num value={row.followers} />
                </td>
                <td className="num">
                  <Num value={row.engagementRate} />
                </td>
                <td className="num">
                  <Num value={row.postsPerWeek} withTooltip={false} />
                </td>
                <td className="num">
                  <Num value={row.postsPerDay} withTooltip={false} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default PerMemberTable;
